import { lazy, Suspense, useEffect, useState, type FormEvent } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CartDrawer from '@/components/feature/CartDrawer';
import { affiliateRequest } from '@/services/affiliates';
import { store } from '@/services/store';
import './affiliates.css';
const AffiliateDashboard = lazy(() => import('./AffiliateDashboard'));

type Dashboard = {
  affiliate: { code: string; name: string; active: number }; enabled: boolean; rate: number;
  earned: number; paid: number; balance: number; lastSync?: number | null; counts?: { total: number; confirmed: number }; clicks?: { total: number; last30: number };
  orders: { order_id: string; rate: number; amount: number; state: string; created: number; synced: number | null }[];
};
const demoOrders = Array.from({length: 28}, (_, i) => ({
  order_id: String(1082 + i), rate: 1000,
  amount: i % 9 === 0 || i % 11 === 0 ? 0 : 1290 + (i * 713) % 4200,
  state: i % 11 === 0 ? 'pending' : i % 9 === 0 ? 'cancelled' : 'confirmed',
  created: Math.floor(new Date(2026, 8, 1 + Math.floor(i * 21 / 28), 12).getTime() / 1000),
  synced: Math.floor(new Date(2026, 8, 22, 12).getTime() / 1000),
})).reverse();
const demoEarned = demoOrders.reduce((sum, order) => sum + order.amount, 0);
const demoDashboard: Dashboard = {
  affiliate: { code: 'ana-demo', name: 'Ana', active: 1 },
  enabled: true, rate: 1000, earned: demoEarned, paid: 25000, balance: demoEarned - 25000,
  orders: demoOrders, lastSync: Math.floor(Date.now() / 1000) - 7 * 60,
};

const steps = [
  ['1', 'Solicite sua participação', 'Fale com a loja pelo WhatsApp. Depois da aprovação, você recebe seu link e um código de acesso privado.'],
  ['2', 'Compartilhe seu link', 'Divulgue as peças da Bozzetti nas suas redes. Cada link é exclusivo e funciona também nas páginas de produto.'],
  ['3', 'Acompanhe suas comissões', 'No seu painel você vê indicações, pedidos, saldo e repasses, tudo em um só lugar.'],
];

const questions = [
  ['Por quanto tempo a indicação vale?', 'Até 30 dias, no mesmo navegador. Se outro link de afiliado for acessado nesse período, vale o mais recente.'],
  ['Sobre qual valor a comissão é calculada?', 'Sobre os produtos, depois dos descontos e sem o frete, em pedidos pagos.'],
  ['O que acontece com cancelamentos e devoluções?', 'Pedidos cancelados ou estornados não geram comissão. Se o estorno vier depois, o valor é descontado do saldo.'],
  ['Como recebo?', 'Os repasses são combinados e conferidos com a loja. Datas e valores ficam registrados no seu painel.'],
];

export default function Affiliates() {
  const demo = import.meta.env.DEV && new URLSearchParams(window.location.search).get('demo') === '1';
  const [access, setAccess] = useState('');
  const [data, setData] = useState<Dashboard | null>(demo ? demoDashboard : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { window.scrollTo(0, 0); document.title = 'Programa de afiliados | Bozzetti Store'; }, []);
  async function login(event?: FormEvent) {
    if (demo) { event?.preventDefault(); setData(demoDashboard); setError(''); return; }
    event?.preventDefault(); setError(''); setBusy(true);
    try { setData(await affiliateRequest({ action: 'dashboard', access: access.trim() })); }
    catch (e) { setError(e instanceof Error ? e.message : 'Não foi possível entrar.'); }
    finally { setBusy(false); }
  }
  if (data) return <Suspense fallback={<div role="status" style={{padding:40}}>Carregando seu painel…</div>}><AffiliateDashboard data={data} demo={demo} busy={busy} error={error} refresh={()=>void login()} logout={()=>{setData(null);setAccess('');setError('');}}/></Suspense>;
  const joinLink = `${store.whatsapp}?text=${encodeURIComponent('Olá! Gostaria de participar do programa de afiliados da Bozzetti Store.')}`;
  return (
    <div className="bz-page">
      <Header />
      <main>
        <section className="bz-aff-hero">
          <div className="bz-aff-hero-copy">
            <p>Programa de afiliados</p>
            <h1>Indique a Bozzetti e ganhe comissão</h1>
            <span>Compartilhe seu link exclusivo e acompanhe as comissões das compras feitas pela sua indicação.</span>
            <div>
              <a className="bz-aff-btn" href={joinLink} target="_blank" rel="noopener noreferrer">Quero participar <ArrowRight size={16} aria-hidden="true" /></a>
              <a className="bz-aff-link" href="#acesso">Já faço parte</a>
            </div>
            <small>Participação sujeita à aprovação da loja e à ativação do programa.</small>
          </div>
          <img src={`${import.meta.env.BASE_URL}editorial/atelier-branco.webp`} alt="Modelo vestindo conjunto de alfaiataria off white" />
        </section>

        <section className="bz-aff-section" aria-labelledby="bz-aff-how">
          <header className="bz-shelf-head"><div><p>Passo a passo</p><h2 id="bz-aff-how">Como funciona</h2></div></header>
          <ol className="bz-aff-steps">
            {steps.map(([number, title, text]) => <li key={number}><b>{number}</b><h3>{title}</h3><p>{text}</p></li>)}
          </ol>
        </section>

        <div className="bz-aff-split">
          <section aria-labelledby="bz-aff-faq">
            <header className="bz-shelf-head"><div><p>Regras do programa</p><h2 id="bz-aff-faq">Dúvidas frequentes</h2></div></header>
            <div className="bz-details">
              {questions.map(([question, answer]) => (
                <details key={question}>
                  <summary>{question} <ChevronDown size={18} strokeWidth={1.5} aria-hidden="true" /></summary>
                  <div className="bz-details-body">{answer}</div>
                </details>
              ))}
            </div>
          </section>

          <section id="acesso" className="bz-aff-login" aria-labelledby="bz-aff-access">
            <h2 id="bz-aff-access">Área do afiliado</h2>
            <p>Use o código privado que você recebeu da loja para ver seus resultados.</p>
            {demo ? (
              <button type="button" className="bz-btn" onClick={() => setData(demoDashboard)}>Abrir painel de demonstração</button>
            ) : (
              <form onSubmit={login}>
                <label htmlFor="affiliate-access">Código de acesso privado</label>
                <input id="affiliate-access" type="password" autoComplete="off" required minLength={64} maxLength={64} value={access} onChange={e => setAccess(e.target.value)} placeholder="Cole seu código de acesso" />
                <button className="bz-btn" disabled={busy}>{busy ? 'Acessando…' : 'Acessar meu painel'}</button>
              </form>
            )}
            {error && <p role="alert" className="bz-form-error">{error}</p>}
            <a href={store.whatsapp} target="_blank" rel="noopener noreferrer">Preciso de ajuda com meu acesso</a>
          </section>
        </div>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
